import React, { memo, useState } from "react";
import { FaCaretLeft, FaCaretDown } from "react-icons/fa";
import Toggle from "../../../../../components/Toggle";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import {
  useDeletePatientQuestionMutation,
  useMarkProblemActiveMutation,
  useMarkProblemInactiveMutation,
} from "../../../../../services/chatService";
import ActionDialog from "../../../../../components/actionDialog";
import { toast } from "react-toastify";
import Tooltip from "../../../../../components/Tooltip";

const titleMapper = {
  delete: "Are you sure you want to Delete this problem?",
  inactive: "Are you sure you want to Activate this problem?",
  active: "Are you sure you want to Deactivate this problem?",
};

function ReportProblemCard({
  problem = {},
  control = {},
  setShowAddProblemDialogue = () => {},
  setSelectedProblem = () => {},
  index = 0,
}) {
  const [problemData, setProblemData] = useState(problem);
  const [showProblemDetails, setShowProblemDetails] = useState(false);
  const [confirmDialogue, setConfirmDialogue] = useState(null);
  const [updatingProblem, setUpdatingProblem] = useState(false);

  const [deleteProblemTrigger] = useDeletePatientQuestionMutation();
  const [markProblemActiveTrigger] = useMarkProblemActiveMutation();
  const [markProblemInactiveTrigger] = useMarkProblemInactiveMutation();

  const handleProblemActive = async () => {
    let newValue = !problemData?.is_active;
    try {
      setProblemData((prev) => ({
        ...prev,
        is_active: newValue,
      }));

      let response = "";
      const checked = newValue;

      if (checked) {
        response = await markProblemActiveTrigger({
          id: problem?.id,
        });
      } else {
        response = await markProblemInactiveTrigger({
          id: problem?.id,
        });
      }

      const { code, msg } = response?.data?.response;
      if (code === 0 || code === "0") {
        toast.success(
          `Nursing Chat problem ${
            !checked ? "Deactivated" : "Activated"
          } successfully`
        );
      } else {
        toast.error(msg || "Error while updating problem");
        setProblemData((prev) => ({
          ...prev,
          is_active: !newValue,
        }));
      }
      setConfirmDialogue(null);
    } catch (error) {
      console.error("Error in handleProblemActive", error);
      toast.error("Error in handleProblemActive");
      setProblemData((prev) => ({
        ...prev,
        is_active: !newValue,
      }));
      setConfirmDialogue(null);
    }
  };

  const handleUpdateProblem = (e) => {
    e.stopPropagation();
    setShowAddProblemDialogue(true);
    setSelectedProblem(problem);
  };

  const handleDeleteProblem = async () => {
    if (updatingProblem) return;
    try {
      setUpdatingProblem(true);
      await deleteProblemTrigger({
        id: problem?.id,
      })
        .then((response) => {
          const { code, msg } = response?.data?.response;
          if (code === 0 || code === "0") {
            toast.success("Nursing Chat problem Deleted successfully");
            setShowAddProblemDialogue(false);
          } else {
            // toast.error(msg || "Error while deleting problem");
          }
        })
        .catch((error) => {
          console.error("Error while deleting problem", error);
          toast.error("Error while deleting problem");
        })
        .finally(() => {
          setUpdatingProblem(false);
          setConfirmDialogue(null);
        });
    } catch (error) {
      toast.error("Error in deleting problem");
      console.log("Error in deleting problem");
      setUpdatingProblem(false);
      setConfirmDialogue(null);
    }
  };

  const handleConfirm = () => {
    if (confirmDialogue === "delete") {
      handleDeleteProblem();
    } else if (confirmDialogue === "active" || confirmDialogue === "inactive") {
      handleProblemActive();
    }
  };

  return (
    <div className="flex flex-col w-full" key={problem?.id}>
      {confirmDialogue && (
        <ActionDialog
          onCancelHandler={() => setConfirmDialogue(null)}
          onConfirmHandler={handleConfirm}
          showModal={confirmDialogue}
          headerTitle="Confirmation"
          title={titleMapper[confirmDialogue]}
          cancelButtonText="No"
          confirmButtonText={
            updatingProblem
              ? confirmDialogue === "delete"
                ? "Deleting"
                : "Updating"
              : "Yes"
          }
          setShowModal={() => setConfirmDialogue(null)}
        />
      )}
      <div
        className={`w-full flex items-center justify-between cursor-pointer border-t border-l border-r border-slate-400 hover:bg-slate-100 p-2 rounded-sm ${
          !showProblemDetails ? " shadow-sm border-b " : "bg-slate-200"
        }`}
        onClick={() => {
          setShowProblemDetails(!showProblemDetails);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setShowProblemDetails(!showProblemDetails);
          }
        }}
        title={showProblemDetails ? "Hide Details" : "Show Details"}
        aria-label={showProblemDetails ? "Hide Details" : "Show Details"}
      >
        <p className="text-sm">{problem?.title}</p>
        <div className="flex items-center gap-3">
          <FiEdit
            title="Edit Problem"
            size={18}
            className="cursor-pointer"
            onClick={(e) => handleUpdateProblem(e)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUpdateProblem(e);
              }
            }}
          />
          <MdDelete
            size={20}
            color="red"
            className="cursor-pointer"
            title="Delete Question"
            aria-label="Delete Question"
            onClick={() => {
              setConfirmDialogue("delete");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setConfirmDialogue("delete");
              }
            }}
          />
          <Tooltip content={!problemData?.is_active ? "Activate Problem" : "Deactivate Problem"} position="top">
            <Toggle
              isOn={problemData?.is_active}
              onToggle={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.target.checked) {
                  setConfirmDialogue("inactive");
                } else {
                  setConfirmDialogue("active");
                }
              }}
              name={`problem_active_${problemData?.id}`}
              title={problemData?.is_active ? "Mark Deactivate" : "Mark Activate"}
            />
          </Tooltip>
          {!showProblemDetails ? (
            <FaCaretLeft className="h-5 w-5" />
          ) : (
            <FaCaretDown className="h-5 w-5" />
          )}
        </div>
      </div>
      {showProblemDetails && (
        <div
          className={`border-b border-l border-r border-slate-400 p-4 flex items-start flex-col gap-2 pl-[40px] ${
            showProblemDetails ? " shadow-sm " : ""
          }`}
        >
          {(problem?.question_json || [])?.map((question, questionIndex) => (
            <div className="flex items-center gap-1">
              <p className="w-fit">{"Q" + (questionIndex + 1) + "."}</p>
              <p>{question?.text || ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(ReportProblemCard);
